import { AnimatePresence, type HTMLMotionProps, motion } from 'framer-motion';
import React from 'react';

// ─── Shared Transitions ─────────────────────────────────────────────────────

const popoverTransition = { duration: 0.15, ease: 'easeOut' as const };
const springBouncy = { type: 'spring' as const, stiffness: 300, damping: 20 };

// ─── FadeIn ─────────────────────────────────────────────────────────────────
// Simple opacity fade-in. Used for loaders, filter bars, table group headers.

export type FadeInProps = HTMLMotionProps<'div'>;

export const FadeIn = React.forwardRef<HTMLDivElement, FadeInProps>(({ children, transition, ...props }, ref) => (
  <motion.div
    ref={ref}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.3, ...transition }}
    {...props}
  >
    {children}
  </motion.div>
));
FadeIn.displayName = 'FadeIn';

// ─── FadeSlideIn ────────────────────────────────────────────────────────────
// Fade + directional slide. The most common animation in the codebase.
// direction: 'up' (default) | 'down' | 'left' | 'right'
// offset: pixel distance to slide from (default 8)

export type FadeSlideInProps = HTMLMotionProps<'div'> & {
  direction?: 'up' | 'down' | 'left' | 'right';
  offset?: number;
  delay?: number;
};

const slideAxis = {
  up: (offset: number) => ({ y: offset }),
  down: (offset: number) => ({ y: -offset }),
  left: (offset: number) => ({ x: -offset }),
  right: (offset: number) => ({ x: offset }),
} as const;

export const FadeSlideIn = React.forwardRef<HTMLDivElement, FadeSlideInProps>(
  ({ children, direction = 'up', offset = 8, delay, transition, initial, animate, ...props }, ref) => {
    const axis = slideAxis[direction](offset);
    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, ...axis, ...(typeof initial === 'object' ? initial : {}) }}
        animate={{ opacity: 1, x: 0, y: 0, ...(typeof animate === 'object' ? animate : {}) }}
        transition={{ duration: 0.3, ease: 'easeOut', delay, ...transition }}
        {...props}
      >
        {children}
      </motion.div>
    );
  }
);
FadeSlideIn.displayName = 'FadeSlideIn';

// ─── PopoverContent ─────────────────────────────────────────────────────────
// Dropdown / popover / select menu entrance animation.
// Used by: DropdownMenu, Select, NativeSelect, FilterSelect, FormDropdown,
//          SelectableItemsCallout, AppsGridMenu, WorkspaceSwitcher, NotificationBell

export type PopoverContentProps = HTMLMotionProps<'div'>;

export const PopoverContent = React.forwardRef<HTMLDivElement, PopoverContentProps>(({ children, ...props }, ref) => (
  <motion.div
    ref={ref}
    initial={{ opacity: 0, y: -4, scale: 0.97 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={popoverTransition}
    {...props}
  >
    {children}
  </motion.div>
));
PopoverContent.displayName = 'PopoverContent';

// ─── ExpandContent ──────────────────────────────────────────────────────────
// Height expand/collapse with fade. Used inside AnimatePresence.
// Used by: ListRowCard, WidgetList, UsersSessionItem, FileUpload

export type ExpandContentProps = HTMLMotionProps<'div'>;

export const ExpandContent = React.forwardRef<HTMLDivElement, ExpandContentProps>(
  ({ children, transition, ...props }, ref) => (
    <motion.div
      ref={ref}
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: 'auto', opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ duration: 0.2, ...transition }}
      {...props}
    >
      {children}
    </motion.div>
  )
);
ExpandContent.displayName = 'ExpandContent';

// ─── ScaleSwap ──────────────────────────────────────────────────────────────
// Scale-based icon swap animation. Used inside AnimatePresence mode="wait".
// Used by: TextWithCopy, CodeBlock (copy/check icon swap)

export type ScaleSwapProps = HTMLMotionProps<'div'>;

export const ScaleSwap = React.forwardRef<HTMLDivElement, ScaleSwapProps>(({ children, ...props }, ref) => (
  <motion.div ref={ref} initial={{ scale: 0.5 }} animate={{ scale: 1 }} exit={{ scale: 0.5 }} {...props}>
    {children}
  </motion.div>
));
ScaleSwap.displayName = 'ScaleSwap';

// ─── ChevronRotate ──────────────────────────────────────────────────────────
// Animated chevron rotation. Wraps a chevron icon.
// Used by: DataList, ListRowCard

export type ChevronRotateProps = Omit<HTMLMotionProps<'div'>, 'animate'> & {
  open: boolean;
  degrees?: number;
};

export const ChevronRotate = React.forwardRef<HTMLDivElement, ChevronRotateProps>(
  ({ children, open, degrees = 90, ...props }, ref) => (
    <motion.div ref={ref} animate={{ rotate: open ? degrees : 0 }} transition={{ duration: 0.2 }} {...props}>
      {children}
    </motion.div>
  )
);
ChevronRotate.displayName = 'ChevronRotate';

// ─── Overlay ────────────────────────────────────────────────────────────────
// Backdrop overlay fade for dialogs, sheets, lightboxes.
// Used by: Dialog, Sheet, ProgressDialog, AIEMPanelView, AppDetailPage

export type OverlayProps = HTMLMotionProps<'div'>;

export const Overlay = React.forwardRef<HTMLDivElement, OverlayProps>(({ children, ...props }, ref) => (
  <motion.div
    ref={ref}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.2 }}
    {...props}
  >
    {children}
  </motion.div>
));
Overlay.displayName = 'Overlay';

// ─── DialogPanel ────────────────────────────────────────────────────────────
// Dialog/modal content animation with scale + centered positioning.
// Used by: Dialog, ProgressDialog

export type DialogPanelProps = HTMLMotionProps<'div'>;

export const DialogPanel = React.forwardRef<HTMLDivElement, DialogPanelProps>(({ children, ...props }, ref) => (
  <motion.div
    ref={ref}
    initial={{ opacity: 0, scale: 0.95, x: '-50%', y: '-48%' }}
    animate={{ opacity: 1, scale: 1, x: '-50%', y: '-50%' }}
    exit={{ opacity: 0, scale: 0.95, x: '-50%', y: '-48%' }}
    transition={{ type: 'spring', stiffness: 350, damping: 30 }}
    {...props}
  >
    {children}
  </motion.div>
));
DialogPanel.displayName = 'DialogPanel';

// ─── SheetPanel ─────────────────────────────────────────────────────────────
// Slide-in from right for sheet/drawer panels.
// Used by: Sheet

export type SheetPanelProps = HTMLMotionProps<'div'>;

export const SheetPanel = React.forwardRef<HTMLDivElement, SheetPanelProps>(({ children, ...props }, ref) => (
  <motion.div
    ref={ref}
    initial={{ x: '100%' }}
    animate={{ x: 0 }}
    exit={{ x: '100%' }}
    transition={{ type: 'spring', damping: 30, stiffness: 300 }}
    {...props}
  >
    {children}
  </motion.div>
));
SheetPanel.displayName = 'SheetPanel';

// ─── ScaleFadeIn ────────────────────────────────────────────────────────────
// Scale + fade entrance. Used for badges, tags, status indicators.
// Used by: StatusTag, TagBadge, FilterSelect badge, SearchInput clear button

export type ScaleFadeInProps = HTMLMotionProps<'div'> & {
  /** Scale to start from (default 0.9) */
  from?: number;
};

export const ScaleFadeIn = React.forwardRef<HTMLDivElement, ScaleFadeInProps>(
  ({ children, from = 0.9, transition, ...props }, ref) => (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: from }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: from }}
      transition={{ duration: 0.2, ...transition }}
      {...props}
    >
      {children}
    </motion.div>
  )
);
ScaleFadeIn.displayName = 'ScaleFadeIn';

// ─── ScalePopIn ─────────────────────────────────────────────────────────────
// Spring-based scale pop for icon containers (EmptyState, ErrorState).
// Used by: EmptyState icon, ErrorState icon

export type ScalePopInProps = HTMLMotionProps<'div'>;

export const ScalePopIn = React.forwardRef<HTMLDivElement, ScalePopInProps>(
  ({ children, transition, ...props }, ref) => (
    <motion.div
      ref={ref}
      initial={{ scale: 0.8 }}
      animate={{ scale: 1 }}
      transition={{ delay: 0.15, ...springBouncy, ...transition }}
      {...props}
    >
      {children}
    </motion.div>
  )
);
ScalePopIn.displayName = 'ScalePopIn';

// ─── StaggerItem ────────────────────────────────────────────────────────────
// Fade + slide with index-based stagger delay. For lists, tables, grids.
// Used by: AppCard, SimpleTable rows, DataList items, AppCardListWidget

export type StaggerItemProps = HTMLMotionProps<'div'> & {
  index: number;
  /** Delay multiplier per index (default 0.03) */
  stagger?: number;
  direction?: 'up' | 'down' | 'left' | 'right';
  offset?: number;
};

export const StaggerItem = React.forwardRef<HTMLDivElement, StaggerItemProps>(
  ({ children, index, stagger = 0.03, direction = 'up', offset = 8, transition, ...props }, ref) => {
    const axis = slideAxis[direction](offset);
    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, ...axis }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ duration: 0.2, delay: index * stagger, ...transition }}
        {...props}
      >
        {children}
      </motion.div>
    );
  }
);
StaggerItem.displayName = 'StaggerItem';

// ─── TabContent ─────────────────────────────────────────────────────────────
// Tab/pivot content switch animation. Used inside AnimatePresence mode="wait".
// Used by: Tabs, AppDetailPage tab content

export type TabContentProps = HTMLMotionProps<'div'>;

export const TabContent = React.forwardRef<HTMLDivElement, TabContentProps>(
  ({ children, transition, ...props }, ref) => (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.2, ...transition }}
      {...props}
    >
      {children}
    </motion.div>
  )
);
TabContent.displayName = 'TabContent';

// ─── ErrorMessage ───────────────────────────────────────────────────────────
// Form error/success message fade-in. Used inside AnimatePresence.
// Used by: Input, FormField, FileUpload

export type ErrorMessageProps = HTMLMotionProps<'p'>;

export const ErrorMessage = React.forwardRef<HTMLParagraphElement, ErrorMessageProps>(
  ({ children, transition, ...props }, ref) => (
    <motion.p
      ref={ref}
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={{ duration: 0.15, ...transition }}
      {...props}
    >
      {children}
    </motion.p>
  )
);
ErrorMessage.displayName = 'ErrorMessage';

// ─── Spinner ────────────────────────────────────────────────────────────────
// Continuous rotation for loading spinners.
// Used by: Loader, Button, SplitButton

export type SpinnerProps = React.ComponentPropsWithRef<typeof motion.svg>;

export const Spinner = React.forwardRef<SVGSVGElement, SpinnerProps>(({ children, transition, ...props }, ref) => (
  <motion.svg
    ref={ref}
    // Intrinsic size, so a spinner cannot stretch to fill its container when the utility classes that
    // normally size it are not in effect — an SVG with a viewBox and no dimensions scales to its parent,
    // which turned a 32px spinner into a page-sized ring in the legacy app, which loads no Tailwind.
    // Presentation attributes lose to any CSS rule, so `h-4 w-4` and callers' classes still win.
    width={24}
    height={24}
    animate={{ rotate: 360 }}
    transition={{ duration: 0.8, repeat: Infinity, ease: 'linear', ...transition }}
    {...props}
  >
    {children}
  </motion.svg>
));
Spinner.displayName = 'Spinner';

// ─── Re-export AnimatePresence for convenience ──────────────────────────────

export { AnimatePresence };
